import { Button, Modal } from '@mantine/core';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import type { RecoveryCodesCount, TotpSetupResponse, TwoFactorStatus } from '@/api';
import { TwoStepCodeInput } from '@/components/Login/TwoStepCodeInput';
import { CopyButton } from '@/components/Ui/CopyButton';
import { EmptyText } from '@/components/Ui/EmptyText';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section, SectionBody } from '@/components/Ui/Section';
import { useUndoToast } from '@/components/Ui/Toast';
import { useIdpHandoff } from '@/hooks/useIdpHandoff';
import { apiOrigin } from '@/lib/origins';
import { isAuthenticatorCode, isTwoStepCode } from '@/lib/two-step-code';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import { errorMessage } from '../format';
import classes from './Security.module.css';

type Dialog =
  | { kind: 'setup'; data: TotpSetupResponse }
  | { kind: 'disable'; methodId: string }
  | { kind: 'regenerate' }
  | { kind: 'codes'; codes: string[] }
  | null;

function CodeDialog({
  intro,
  confirmLabel,
  danger,
  appOnly,
  onConfirm,
  onClose,
  children,
}: {
  intro: string;
  appOnly?: boolean;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: (code: string) => Promise<void>;
  onClose: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = appOnly ? isAuthenticatorCode(code) : isTwoStepCode(code);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(code);
    } catch (e) {
      setError(
        errorMessage(e) ??
          t(appOnly ? 'security.twoStep.wrongAppCode' : 'security.twoStep.wrongCode')
      );
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={shared.stack} style={{ gap: 14 }}>
      {children}
      <span className={shared.sub}>{intro}</span>
      <TwoStepCodeInput
        value={code}
        onChange={setCode}
        onSubmit={submit}
        disabled={busy}
        error={error}
        autoFocus
        appOnly={appOnly}
      />
      <div className={shared.row}>
        <Button
          variant={danger ? 'danger' : 'filled'}
          onClick={submit}
          loading={busy}
          disabled={!valid}
        >
          {confirmLabel}
        </Button>
        <Button variant="default" onClick={onClose} disabled={busy}>
          {t('ui.cancel')}
        </Button>
      </div>
    </div>
  );
}

export function TwoStepSection() {
  const { t } = useTranslation();
  const toast = useUndoToast();
  const api = useRustyState.getState().api;
  const { buildUrl, accountManagementUri } = useIdpHandoff(apiOrigin());

  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [codes, setCodes] = useState<RecoveryCodesCount | null>(null);
  const [failed, setFailed] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [starting, setStarting] = useState(false);
  const [ssoLinked, setSsoLinked] = useState(false);

  const load = useCallback(async () => {
    const state = useRustyState.getState();
    try {
      const s = await state.api.getTwoFactorStatus();
      setStatus(s);
      setFailed(false);
      if (s.enabled) {
        setCodes(await state.api.getRecoveryCodesCount().catch(() => null));
      }
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
    useRustyState
      .getState()
      .api.listIdentities()
      .then((list) => setSsoLinked(list.some((i) => i.method === 'Oidc')))
      .catch(() => setSsoLinked(false));
  }, [load]);

  const startSetup = async () => {
    setStarting(true);
    try {
      const data = await api.setupTotp(undefined, { onSuccess: false });
      setDialog({ kind: 'setup', data });
    } finally {
      setStarting(false);
    }
  };

  const returnTo = `${window.location.origin}${window.location.pathname}${window.location.hash.split('?')[0]}?refreshed=1`;
  const ssoUrl = buildUrl('2fa', returnTo) ?? accountManagementUri;

  const method = status?.methods[0];

  return (
    <Section title={t('security.twoStep.title')} subtitle={t('security.twoStep.appliesTo')}>
      {failed ? (
        <SectionBody>
          <p className={shared.errorText}>{t('security.twoStep.loadFailed')}</p>
        </SectionBody>
      ) : status === null ? (
        <SectionBody>
          <EmptyText>{t('ui.loading')}</EmptyText>
        </SectionBody>
      ) : status.enabled ? (
        <>
          <LedgerRow
            label={t('security.twoStep.status')}
            value={t('security.twoStep.on')}
            help={t('security.twoStep.onHelp')}
            action={
              method && (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setDialog({ kind: 'disable', methodId: method.id })}
                >
                  {t('security.twoStep.turnOff')}
                </Button>
              )
            }
          />
          <LedgerRow
            label={t('security.twoStep.recovery')}
            value={
              codes
                ? t('security.twoStep.recoveryLeft', { left: codes.remaining, total: codes.total })
                : t('security.twoStep.recoveryLeftShort', { left: status.recovery_codes_remaining })
            }
            help={t('security.twoStep.recoveryHelp')}
            action={
              <Button variant="subtle" size="xs" onClick={() => setDialog({ kind: 'regenerate' })}>
                {t('security.twoStep.newCodes')}
              </Button>
            }
          />
        </>
      ) : (
        <LedgerRow
          label={t('security.twoStep.status')}
          value={t('security.twoStep.off')}
          help={t('security.twoStep.offHelp')}
          action={
            <Button variant="default" size="xs" onClick={startSetup} loading={starting}>
              {t('security.twoStep.turnOn')}
            </Button>
          }
        />
      )}

      {ssoLinked && (
        <LedgerRow
          label={t('security.twoStep.sso')}
          value={t('security.twoStep.ssoValue')}
          help={t('security.twoStep.ssoHelp')}
          action={
            ssoUrl ? (
              <Button component="a" href={ssoUrl} variant="subtle" size="xs">
                {t('security.twoStep.manageAtSso')}
              </Button>
            ) : undefined
          }
        />
      )}

      <Modal
        opened={dialog !== null}
        onClose={() => setDialog(null)}
        closeOnClickOutside={dialog?.kind !== 'codes'}
        title={
          dialog?.kind === 'setup'
            ? t('security.twoStep.setupTitle')
            : dialog?.kind === 'disable'
              ? t('security.twoStep.disableTitle')
              : dialog?.kind === 'regenerate'
                ? t('security.twoStep.regenerateTitle')
                : t('security.twoStep.codesTitle')
        }
      >
        {dialog?.kind === 'setup' && (
          <CodeDialog
            intro={t('security.twoStep.setupCode')}
            confirmLabel={t('security.twoStep.setupConfirm')}
            appOnly
            onClose={() => setDialog(null)}
            onConfirm={async (code) => {
              const res = await api.verifyTotpSetup(dialog.data.setup_token, code, {
                onSuccess: false,
                onError: false,
              });
              await load();
              setDialog({ kind: 'codes', codes: res.recovery_codes });
            }}
          >
            <span className={shared.sub}>{t('security.twoStep.setupScan')}</span>
            <div className={classes.qr}>
              <QRCode value={dialog.data.otpauth_uri} size={180} />
            </div>
            <span className={shared.help}>{t('security.twoStep.setupManual')}</span>
            <div className={shared.row}>
              <code className={shared.secret}>{dialog.data.secret}</code>
              <CopyButton text={dialog.data.secret} />
            </div>
          </CodeDialog>
        )}
        {dialog?.kind === 'disable' && (
          <CodeDialog
            intro={t('security.twoStep.disableBody')}
            confirmLabel={t('security.twoStep.disableConfirm')}
            danger
            onClose={() => setDialog(null)}
            onConfirm={async (code) => {
              await api.disableTwoFactorMethod(dialog.methodId, code, { onError: false });
              setDialog(null);
              setCodes(null);
              toast(t('security.twoStep.disabled'));
              await load();
            }}
          />
        )}
        {dialog?.kind === 'regenerate' && (
          <CodeDialog
            intro={t('security.twoStep.regenerateBody')}
            confirmLabel={t('security.twoStep.regenerateConfirm')}
            onClose={() => setDialog(null)}
            onConfirm={async (code) => {
              const res = await api.regenerateRecoveryCodes(code, {
                onSuccess: false,
                onError: false,
              });
              await load();
              setDialog({ kind: 'codes', codes: res.codes });
            }}
          />
        )}
        {dialog?.kind === 'codes' && (
          <div className={shared.stack} style={{ gap: 14 }}>
            <strong>{t('security.twoStep.codesSave')}</strong>
            <ul className={classes.codes}>
              {dialog.codes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <span className={shared.help}>{t('security.twoStep.codesHelp')}</span>
            <div className={shared.row}>
              <CopyButton
                text={dialog.codes.join('\n')}
                label={t('security.twoStep.copyAll')}
                size="sm"
              />
              <Button onClick={() => setDialog(null)}>{t('security.twoStep.codesDone')}</Button>
            </div>
          </div>
        )}
      </Modal>
    </Section>
  );
}
